import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { WorkTrackingAuslage } from '@chancenwerk/work-tracking/entitites/WorkTrackingAuslage.js';
import { WorkTrackingChange } from '@chancenwerk/work-tracking/entitites/WorkTrackingChange.js';
import { WorkTrackingTaetigkeit } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeitenState.js';
import { html, nothing } from 'lit';
import { ChromiumDialog } from '../../client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '../../client/design/components/cr-snackbar.js';

export function stateIcon(s: WorkTrackingTaetigkeitenState) {
  switch (s) {
    case WorkTrackingTaetigkeitenState.OPEN:
      return 'question_mark';
    case WorkTrackingTaetigkeitenState.FINISHED:
      return 'check';
    case WorkTrackingTaetigkeitenState.QUESTION:
      return 'warning';
  }
}

export function stateColor(e: ChemistryLitElement, s: WorkTrackingTaetigkeitenState) {
  switch (s) {
    case WorkTrackingTaetigkeitenState.OPEN:
      return e.colors.neutral;
    case WorkTrackingTaetigkeitenState.FINISHED:
      return e.colors.tone.success;
    case WorkTrackingTaetigkeitenState.QUESTION:
      return e.colors.tone.warning;
  }
}

export function workTrackingStateTemplate(e: ChemistryLitElement, s: WorkTrackingTaetigkeitenState) {
  return html`
    <cr-flex horizontal space="sm" crossaxis-center ${css({ color: stateColor(e, s) })}>
      <cr-icon> ${stateIcon(s)} </cr-icon>
      <cr-text> ${s} </cr-text>
    </cr-flex>
  `;
}

export function createPrefixColumns<T extends WorkTrackingTaetigkeit | WorkTrackingAuslage>(
  element: ChemistryLitElement,
  isSelf: () => boolean,
) {
  return [
    {
      fieldName: 'stateResponsible',
      columnName: 'Verwaltung',
      columnWidth: 'max-content',
      renderItem: (t: T) => {
        return workTrackingStateTemplate(element, t.stateResponsible ?? WorkTrackingTaetigkeitenState.OPEN);
      },
    },
    {
      fieldName: 'stateContractor',
      columnName: isSelf() ? 'Du' : 'Tätiger',
      columnWidth: '120px',
      renderItem: (t: T) => {
        return workTrackingStateTemplate(element, t.stateContractor ?? WorkTrackingTaetigkeitenState.OPEN);
      },
    },
    {
      fieldName: 'date',
      columnName: 'Datum',
      getFieldMapping: (t: T) => t.date?.toFormat('dd.MM.yyyy'),
    },
  ];
}

export function createMoneyColumn<T extends WorkTrackingTaetigkeit | WorkTrackingAuslage>(
  element: ChemistryLitElement,
) {
  return {
    fieldName: 'money',
    selectionSet: { money: 1, disabled: 1 },
    columnName: '€',
    columnWidth: '100px',
    getFieldMapping: (t: T) => {
      if (t instanceof WorkTrackingTaetigkeit && t.disabled)
        return html`<cr-text
          ${css({ fontFamily: 'monospace', color: element.colors.tone.error, textDecoration: 'line-through' })}
        >
          ${(t.money ?? 0).toFixed(2)}€
        </cr-text>`;
      return html`<cr-text ${css({ fontFamily: 'monospace' })}>${(t.money ?? 0).toFixed(2)}€</cr-text>`;
    },
  };
}

export function createActionButtons(
  element: ChemistryLitElement,
  finishedButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  questionButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  editButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  disableButton: (t: WorkTrackingTaetigkeit) => unknown,
  historyButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  canSeeAll: () => boolean,
) {
  return {
    customID: '_actions',
    columnName: 'Aktionen',
    columnWidth: 'max-content',
    selectionSet: {
      userID: 1,
      changes: { ...WorkTrackingChange.allFields, from: { vorname: 1, nachname: 1, email: 1 } },
    },
    renderItem: (t: WorkTrackingTaetigkeit) => {
      return html`
        <cr-flex horizontal crossaxis-center space="md" padding="sm">
          ${finishedButton(t)} ${questionButton(t)} ${canSeeAll() ? editButton(t) : nothing} ${disableButton(t)}
          ${historyButton(t)}
        </cr-flex>
      `;
    },
  };
}

export function createAuslageActionButtons(
  element: ChemistryLitElement,
  finishedButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  questionButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  editButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
  historyButton: (t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => unknown,
) {
  return {
    customID: '_',
    columnName: 'Aktionen',
    columnWidth: 'max-content',
    selectionSet: { userID: 1 },
    renderItem: (t: WorkTrackingAuslage) => {
      return html`<cr-flex horizontal crossaxis-center space="md" padding="sm">
        ${finishedButton(t)} ${questionButton(t)} ${editButton(t)}
        ${historyButton(t)} 
        <cr-button
          mode="icon"
          icon="delete"
          ${css({ color: element.colors.tone.error, scale: '1.4' })}
          @click=${async () => {
            await t.remove();
          }}
        >
          <cr-tooltip><cr-text>Löschen<cr-text></cr-tooltip>
        </cr-button>
      </cr-flex>`;
    },
  };
}

export function createLastNoteColumn() {
  return {
    customID: '_lastNote',
    columnName: 'Letzte Notiz',
    columnWidth: '300px',
    selectionSet: {
      note: 1,
      changes: { ...WorkTrackingChange.allFields, from: { vorname: 1, nachname: 1, email: 1 } },
    },
    getFieldMapping: (e: WorkTrackingTaetigkeit | WorkTrackingAuslage) => e.lastNote ?? '',
  };
}

export function createAuslageLastNoteColumn() {
  return {
    customID: '_lastNote',
    columnName: 'Letzte Notiz',
    columnWidth: '300px',
    selectionSet: {
      note: 1,
      changes: { ...WorkTrackingChange.allFields, from: { vorname: 1, nachname: 1, email: 1 } },
    },
    renderItem: (t: WorkTrackingAuslage) => html`<cr-text>${t.lastNote}</cr-text>`,
  };
}

export function createKpfColumn() {
  return {
    fieldName: 'kpfID',
    columnName: 'Standort',
    selectionSet: {
      kpfID: 1,
      kpfName: 1,
    },
    getFieldMapping: (t: WorkTrackingTaetigkeit) => {
      return t.kpfName ?? '';
    },
  };
}

export function createAuslageKpfColumn() {
  return {
    fieldName: 'kpfID',
    columnName: 'Standort',
    selectionSet: {
      kpfID: 1,
      kpf: { kuerzel: 1, produkt: { kuerzel: 1 }, school: { kuerzel: 1, name: 1 } },
      receipt: {
        meta: {
          fileName: 1,
        },
      },
    },
    getFieldMapping: (t: WorkTrackingAuslage) => t.kpf?.toString() ?? '',
  };
}

export function createTimeColumn() {
  return {
    fieldName: 'startTime',
    columnName: 'Uhrzeit',
    selectionSet: { startTime: 1, endTime: 1 },
    getFieldMapping: (t: WorkTrackingTaetigkeit) =>
      t.startTime && t.endTime ? `${t.startTime?.toFormat('HH:mm')} - ${t.endTime?.toFormat('HH:mm')}` : '',
  };
}

export function createReceiptColumn(element: ChemistryLitElement, currentUserID: () => Maybe<string>) {
  return {
    customID: '_quitting',
    columnName: 'Quittung',
    selectionSet: { receiptID: 1, receipt: AdornisFile.allFields },
    renderItem: (t: WorkTrackingAuslage) => {
      return html`<cr-flex horizontal crossaxis-center space="lg" ${css({
        padding: `0 ${element.spacing.md}`,
      })}>
      <cr-flex padding="xs"
      ${css({
        backgroundColor: element.colors.neutral.shade(200),
        borderRadius: element.sizes.borderRadiusSecondary,
      })}>
      <cr-button mode="icon" icon="upload" tone="${t.receiptID ? 'neutral' : 'error'}" 
      @click=${async () => {
        const file = await askForFile({ acceptMultipleFiles: false });
        if (!file?.[0]) {
          void ChromiumSnackbar.show('Beim Upload ist etwas schiefgegangen!');
          return;
        }

        t.receiptID = await AdornisFile.upload(file[0]);
        t.addChanges(currentUserID() ?? '', t, file?.[0].name + ' hochgeladen');
        await t.save();

        await ChromiumDialog.showPopup({
          content(resolve, reject, dialogElement) {
            return html`<cr-flex padding="lg" space="md">
              <cr-text bold>Bitte sende das Original in die Zentrale. Adresse:</cr-text>
              <cr-text>Chancenwerk e.V., z. Hd. Ergün Yalcin, Am Markt 18, 44575 Castrop-Rauxel</cr-text>
              <cr-button @click=${resolve} ${css({ maxWidth: '150px' })}>Ok</cr-button>
            </cr-flex>`;
          },
        });
      }}>
        <cr-tooltip><cr-text>Hochladen<cr-text></cr-tooltip>
      </cr-button>
      </cr-flex>

      ${
        t.receiptID
          ? html`
          <cr-flex horizontal crossaxis-center padding="xs" space="sm" ${css({
            backgroundColor: element.colors.primary.shade(100),
            borderRadius: element.sizes.borderRadiusSecondary,
            paddingRight: element.spacing.md,
          })}>
            <cr-button mode="icon" icon="download" @click=${async () => {
              const res = t.receipt?.getDownloadLink();
              if (!res) {
                return;
              }
              window.open(res, '_blank');
            }}>
          <cr-tooltip><cr-text>Runterladen<cr-text></cr-tooltip>
        </cr-button>
        <cr-text>${t.receipt?.meta?.fileName}</cr-text>
        </cr-flex>
      `
          : nothing
      }
    </cr-flex>`;
    },
  };
}
